import heroKobePort from "../../../assets/hero-kobe-port.jpg";
import logoMinatomachi from "../../../assets/logo-minatomachi.png";
import heroCurryReal from "../../../assets/hero-curry-real.jpg";
import { STORE } from "../../../data/storeData";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative w-full min-h-[720px] lg:min-h-[820px] overflow-hidden"
    >
      {/* Background image: Kobe port tower real photo */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${heroKobePort})`,
        }}
      />
      {/* Subtle overlay for readability - lighter since photo is bright */}
      <div className="absolute inset-0 bg-gradient-to-b from-secondary-950/30 via-secondary-900/15 to-secondary-950/40" />

      {/* Decorative anchors */}
      <i className="ri-anchor-line hidden md:block absolute left-8 top-32 text-background-50/70 text-6xl animate-swing" />
      <i className="ri-anchor-line hidden md:block absolute right-16 bottom-40 text-background-50/50 text-5xl animate-swing" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 md:pt-32 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        {/* Left: Title */}
        <div className="text-background-50 animate-fadeup">
          {/* タグライン */}
          <p
            className="inline-block font-heading font-bold text-xs md:text-sm tracking-wide bg-background-50/15 border border-background-50/40 rounded-full px-3 py-1 mb-4 backdrop-blur-sm animate-fadeup"
            style={{ animationDelay: '0.05s', animationFillMode: 'both' }}
          >
            {STORE.tagline}
          </p>
          <p
            className="font-sans text-xl md:text-3xl lg:text-4xl font-black drop-shadow-lg mb-6 animate-fadeup tracking-wide"
            style={{ animationDelay: '0.2s', animationFillMode: 'both' }}
          >
            港町の活気を、<br />豪快な肉カレーで味わう
          </p>

          {/* Logo */}
          <h1 className="font-heading font-black text-5xl md:text-7xl leading-[1.05] tracking-tight drop-shadow-xl mb-6">
            <img
              src={logoMinatomachi}
              alt="KOBEミナトマチカレー"
              width={1253}
              height={425}
              fetchPriority="high"
              decoding="async"
              className="w-full max-w-[380px] md:max-w-[480px] drop-shadow-2xl"
              style={{ filter: 'drop-shadow(0 0 2px rgba(255,255,255,1)) drop-shadow(0 0 2px rgba(255,255,255,1)) drop-shadow(0 0 4px rgba(255,255,255,0.95)) drop-shadow(0 4px 8px rgba(0,0,0,0.4))' }}
            />
          </h1>

          {/* 営業情報カード（営業中／営業時間／場所） */}
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="bg-background-50 text-primary-600 rounded-2xl px-7 py-5 md:px-9 md:py-6 border-4 border-primary-600 rotate-[-2deg] animate-floaty shadow-xl max-w-[340px] md:max-w-[380px]">
              {/* ブランドコピー — wave reveal */}
              <p
                className="font-heading font-black text-primary-700 text-sm md:text-base leading-snug mb-3"
                style={{ WebkitTextStroke: "0.4px currentColor" }}
              >
                {STORE.brandCopy.split("").map((ch, i) => (
                  <span
                    key={i}
                    className="animate-char-wave"
                    style={{ animationDelay: `${0.2 + i * 0.05}s` }}
                  >
                    {ch}
                  </span>
                ))}
              </p>

              <p className="inline-flex items-center gap-1.5 bg-primary-600 text-background-50 rounded-full px-3 py-1 font-heading font-black text-sm md:text-base">
                <span className="w-2 h-2 rounded-full bg-accent-400" aria-hidden="true" />
                {STORE.statusLabel}
              </p>

              <p className="font-label text-primary-600 text-xl md:text-2xl tracking-widest leading-none mt-4">
                OPEN
              </p>
              <p className="font-heading font-black text-3xl md:text-4xl leading-none mt-1 whitespace-nowrap">
                {STORE.hoursOpen}
                <span className="text-xl md:text-2xl mx-1">〜</span>
                {STORE.hoursClose}
              </p>
              <p className="font-heading font-bold text-primary-700 text-sm md:text-base mt-2">
                {STORE.lastOrderLabel}／{STORE.takeoutLabel}
              </p>
            </div>
            <div className="text-background-50">
              <p className="font-heading font-bold text-base md:text-lg drop-shadow">
                {STORE.facilityName}
              </p>
              <p className="font-heading text-sm md:text-base text-accent-200">
                {STORE.floorLabel}
              </p>
              <p className="font-heading text-sm md:text-base text-background-50/90 mt-1 flex items-start gap-1.5">
                <i className="ri-map-pin-2-line mt-0.5" aria-hidden="true"></i>
                {STORE.postalCode} {STORE.address}
              </p>
              <p className="font-heading text-sm md:text-base text-background-50/90 mt-1 flex items-center gap-1.5">
                <i className="ri-calendar-close-line" aria-hidden="true"></i>
                定休日：{STORE.regularHoliday}
              </p>
              <a
                href={`tel:${STORE.phone}`}
                className="font-heading text-sm md:text-base text-background-50 mt-1 flex items-center gap-1.5 underline underline-offset-2 cursor-pointer"
              >
                <i className="ri-phone-line" aria-hidden="true"></i>
                {STORE.phoneDisplay}
              </a>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#menu"
              className="inline-flex items-center justify-center gap-2 h-12 min-h-[44px] px-6 rounded-full bg-primary-600 hover:bg-primary-700 text-background-50 dark:text-foreground-950 font-heading font-bold whitespace-nowrap cursor-pointer transition shadow-lg"
            >
              <i className="ri-restaurant-line" aria-hidden="true"></i>
              メニューを見る
            </a>
            <a
              href={STORE.googleMapsSearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-12 min-h-[44px] px-6 rounded-full bg-background-50/95 hover:bg-background-50 text-primary-600 font-heading font-bold whitespace-nowrap cursor-pointer transition"
            >
              <i className="ri-map-2-line" aria-hidden="true"></i>
              Googleマップで見る
            </a>
            <a
              href={`tel:${STORE.phone}`}
              className="inline-flex items-center justify-center gap-2 h-12 min-h-[44px] px-6 rounded-full border-2 border-background-50/90 text-background-50 hover:bg-background-50/15 font-heading font-bold whitespace-nowrap cursor-pointer transition"
            >
              <i className="ri-phone-line" aria-hidden="true"></i>
              電話する
            </a>
          </div>
        </div>

        {/* Right: Curry photo */}
        <div className="relative animate-fadeup">
          <div className="relative w-full max-w-[560px] mx-auto">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-10 bg-background-50 text-primary-600 border-4 border-primary-600 rounded-full px-5 py-2 font-heading font-black text-sm md:text-base whitespace-nowrap rotate-[-3deg]">
              — KOBEミナトマチカレー —
            </div>
            <div className="rounded-full overflow-hidden aspect-square border-8 border-background-50 bg-background-50">
              <img
                src={heroCurryReal}
                alt="KOBEミナトマチカレー 看板メニューの肉カレー"
                width={1100}
                height={733}
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover object-center"
              />
            </div>
            {/* Sticker */}
            <div className="absolute -right-4 md:-right-8 bottom-8 w-28 h-28 md:w-32 md:h-32 rounded-full bg-accent-500 text-secondary-900 flex flex-col items-center justify-center font-heading font-black text-center rotate-[8deg] border-4 border-background-50 animate-floaty px-1">
              <span className="text-[10px] md:text-xs leading-tight whitespace-nowrap">
                まるごとかける、
              </span>
              <span className="text-[10px] md:text-xs leading-tight whitespace-nowrap">
                港町のごちそう
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Rope divider bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-6 rope-border opacity-90" />
    </section>
  );
}
